import { useAtomValue } from 'jotai';

import { ListViewGroup, ListViewRow, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { InfoLineItem } from '@common/components/InfoLineItem';
import { PricedLineItem } from '@common/components/PricedLineItem';
import { SpecsListView } from '@common/components/SpecsListView';

import { useGetOrderDetails } from '../../api/useGetOrderDetails';
import { orderDetailsDrawerAtom } from '../../atoms/orderDetailsDrawerAtoms';

import { SectionSkeleton } from './shared/SectionSkeleton';

const SECTION_TITLE = 'Vehicle details';

interface VehicleDetailsContentProps {
	purchasedVehicleId: string;
	condensed?: boolean;
}

export const VehicleDetailsContent = ({
	purchasedVehicleId,
	condensed = false,
}: VehicleDetailsContentProps) => {
	const { data: orderDetails } = useGetOrderDetails(purchasedVehicleId);

	if (!orderDetails) {
		return null;
	}

	const { vehicleDetails } = orderDetails;
	const { vehicle, specs, additionalDetails } = vehicleDetails;

	const VehicleSubsection = () => {
		return (
			<>
				<Text size='footnote' weight='semibold'>
					Vehicle
				</Text>
				<ListViewGroup>
					<ListViewRow
						bottomContent={
							<PricedLineItem
								imageUrl={vehicle.imgUrl}
								primaryLabel={vehicle.name}
								secondaryLabel={vehicle.vin}
								price={vehicle.totalPrice}
							/>
						}
					/>
				</ListViewGroup>
			</>
		);
	};

	const VehicleSpecsSubsection = () => (
		<SpecsListView specs={specs} emptyMessage='No specs on this order' />
	);

	const VehicleAdditionalDetailsSubsection = () => {
		const formattedEstProdDate = additionalDetails.estimatedProductionDate || undefined;

		const odometerReading = additionalDetails.odometerReading
			? additionalDetails.odometerReading.toString()
			: undefined;
		const engineNumber = additionalDetails.engineNumber;
		const make = additionalDetails.make;
		const year = additionalDetails.year?.toString();
		const stockNumber = additionalDetails.stockNumber;

		return (
			<>
				<Text size='footnote' weight='semibold'>
					Additional details
				</Text>
				<InfoLineItem title='VIN' value={additionalDetails.vin} isCopyable />
				<InfoLineItem title='Engine number' value={engineNumber} isCopyable />
				<InfoLineItem title='Odometer reading' value={odometerReading} />
				<InfoLineItem title='Est. production date' value={formattedEstProdDate} />
				<InfoLineItem title='Make' value={make} />
				<InfoLineItem title='Year' value={year} />
				<InfoLineItem title='Stock number' value={stockNumber} isCopyable />
			</>
		);
	};

	return (
		<Stack itemsSpacing='16'>
			<VehicleSubsection />
			<VehicleSpecsSubsection />
			{!condensed && <VehicleAdditionalDetailsSubsection />}
		</Stack>
	);
};

export const VehicleDetailsSection = () => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const { data: orderDetails, isLoading } = useGetOrderDetails(
		selectedRow?.purchasedVehicleId || undefined,
	);

	if (isLoading) {
		return <SectionSkeleton title={SECTION_TITLE} />;
	}

	if (!selectedRow?.purchasedVehicleId || !orderDetails) {
		return null;
	}

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<VehicleDetailsContent purchasedVehicleId={selectedRow.purchasedVehicleId} />
		</AccordionSection>
	);
};
